/**
* PCC (Police Clearance Certificate) content generator — deterministic,
* seeded template engine. Produces 1,600-2,200 word unique HTML per
* (service × city × destination × locale) combo via mulberry32+FNV-1a,
* so SSR/CSR match and no two pages share the same body.
*/
import type { PccService, PccCity } from "@/data/pcc";

// ── PRNG ─────────────────────────────────────────────────────────────────
function fnv1a(str: string): number {
  let h = 2166136261;
  for (let i = 0; i < str.length; i++) { h ^= str.charCodeAt(i); h = Math.imul(h, 16777619); }
  return h >>> 0;
}
function mulberry32(seed: number) {
  let t = seed >>> 0;
  return () => {
    t = (t + 0x6D2B79F5) >>> 0;
    let r = Math.imul(t ^ (t >>> 15), 1 | t);
    r = (r + Math.imul(r ^ (r >>> 7), 61 | r)) ^ r;
    return ((r ^ (r >>> 14)) >>> 0) / 4294967296;
  };
}
export function makeRng(seedStr: string) { return mulberry32(fnv1a(seedStr)); }
export function pick<T>(rng: () => number, arr: readonly T[]): T { return arr[Math.floor(rng() * arr.length)]!; }
export function shuffle<T>(rng: () => number, arr: readonly T[]): T[] {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) { const j = Math.floor(rng() * (i + 1)); [a[i], a[j]] = [a[j]!, a[i]!]; }
  return a;
}

// ── Rotation pools ───────────────────────────────────────────────────────
const HERO_ANGLES = [
  "ตรวจขั้นตอนกับกองทะเบียนก่อนยื่น", "เตรียมเอกสารตามประเภทคำร้อง",
  "ครบวงจร PCC + MFA + Embassy", "จัดส่งเอกสารตามพื้นที่และเงื่อนไขผู้ขนส่ง",
  "ตรวจความจำเป็นของล่ามก่อนวันนัด", "ตรวจข้อมูลตามเอกสารต้นฉบับ",
  "รองรับผู้ขอหลายสัญชาติ", "ตรวจช่องทางนัดหมายทางการ",
  "ตรวจข้อกำหนดหน่วยงานปลายทาง", "จำกัดการเข้าถึงเอกสารตามหน้าที่",
  "ประสาน POA รับ PCC จากต่างประเทศ",
];

const TRUST_HOOKS = [
  "ตรวจขั้นตอนและเอกสารสำหรับยื่นต่อกองทะเบียนประวัติอาชญากร (กปอ.) สำนักงานตำรวจแห่งชาติ",
  "ตรวจแบบคำร้องและข้อมูลภาษาอังกฤษให้ตรงกับพาสปอร์ตก่อนวันนัด",
  "QA 3 ชั้น — ตรวจสะกดชื่อพาสปอร์ต / เลขบัตรประชาชน / วันเดือนปีเกิด / ที่อยู่ตามฟอร์มสถานทูตปลายทาง",
  "ตรวจ checklist ของหน่วยงานผู้รับตามข้อมูลล่าสุดก่อนจัดชุดเอกสาร โดยผลการพิจารณาเป็นอำนาจของหน่วยงานนั้น",
  "แจ้งจุดติดตามและสถานะที่ตรวจสอบได้ตามช่องทางของหน่วยงาน",
];

const H2_POOL = [
  "ใบตรวจประวัติอาชญากรรม (PCC) คืออะไร",
  "ทำไมต้องใช้บริการตัวแทน — เทียบไปเองกับให้เราทำแทน",
  "ขั้นตอนขอ PCC แบบละเอียดทีละสเต็ป",
  "เอกสารที่ต้องเตรียมสำหรับคนไทย vs ชาวต่างชาติ",
  "ราคาและ Turnaround — เลือกแบบไหนเหมาะกับคุณ",
  "Type 1 vs Type 2 — ต่างกันอย่างไร ควรเลือกอันไหน",
  "PCC + แปลรับรอง + MFA + Legalization",
  "PCC สำหรับยื่นวีซ่าอเมริกา / อังกฤษ / ออสเตรเลีย / แคนาดา",
  "PCC สำหรับ Expat / ครู / นักลงทุน LTR / Elite Visa",
  "ขอ PCC ไทยจากต่างประเทศ (Power of Attorney)",
  "Corporate Bulk PCC สำหรับ HR / Migration Agent",
  "FAQ ที่ลูกค้าถามบ่อยที่สุด",
  "ตัวอย่างสถานการณ์และจุดที่ควรตรวจสอบ",
  "วิธีตรวจสอบเอกสาร PCC ของแท้",
  "ข้อผิดพลาดที่พบบ่อย และวิธีหลีกเลี่ยง",
  "อายุการใช้งาน PCC ในแต่ละประเทศ",
  "workflow ภายในของเราตั้งแต่รับงานถึงส่งมอบ",
  "จุดรับงานและเวลาทำการ",
];

const STEP_POOL = [
  { th: "แจ้งงานทาง LINE / Email พร้อมส่งสำเนาพาสปอร์ต+บัตรประชาชน+แบบฟอร์มปลายทาง", tag: "แจ้งงาน" },
  { th: "ทีมตรวจไฟล์และแจ้งขอบเขต ราคา กำหนดส่ง และรายการเอกสารเพิ่มเติม", tag: "ประเมิน" },
  { th: "ยืนยันงาน + ชำระค่าบริการ (โอน KBank/SCB, PromptPay, บัตรเครดิต, บริษัทออก invoice)", tag: "ยืนยัน" },
  { th: "จองคิว online กับกองทะเบียนประวัติอาชญากร หรือนัดหมาย escort ที่ปทุมวัน", tag: "จองคิว" },
  { th: "ยืนยันสถานที่และวิธีพิมพ์ลายนิ้วมือตามประเภทคำร้อง", tag: "Fingerprinting" },
  { th: "ยื่นคำร้อง Type 1 / Type 2 + ค่าธรรมเนียม 100 บาท ที่เคาน์เตอร์ กปอ.", tag: "ยื่นคำร้อง" },
  { th: "ติดตามสถานะตามช่องทางที่หน่วยงานเปิดให้ และแจ้งข้อมูลเมื่อมีการเปลี่ยนแปลง", tag: "ติดตาม" },
  { th: "รับเอกสารต้นฉบับ + ตรวจสอบชื่อ/วันเกิด/passport number ก่อนส่งมอบ", tag: "QA" },
  { th: "แปลอังกฤษ + รับรอง (NAATI / กงสุล / สาบาน) ถ้าปลายทางต้องการ", tag: "แปล" },
  { th: "รับรอง MFA (กรมการกงสุล ถ.แจ้งวัฒนะ) + Embassy legalization ถ้าจำเป็น", tag: "Legalization" },
  { th: "ส่งมอบต้นฉบับ + สแกน PDF ผ่าน EMS / Kerry / Grab / มารับที่สำนักงาน", tag: "ส่งมอบ" },
];

const FAQ_POOL = [
  { q: "ใบตรวจประวัติอาชญากรรม (PCC) ต่างจากหนังสือรับรองความประพฤติอย่างไร", a: "เป็นเอกสารเดียวกันในทางปฏิบัติ — ออกโดยกองทะเบียนประวัติอาชญากร สำนักงานตำรวจแห่งชาติ (กปอ.สตช.) มี 2 แบบ: Type 1 (สรุปว่ามี/ไม่มีประวัติ) และ Type 2 (Full Disclosure ระบุคดีทั้งหมด)" },
  { q: "ราคาบริการเท่าไหร่", a: "ส่งเอกสารให้ทีมตรวจเพื่อประเมินขอบเขต ค่าบริการ และระยะเวลาดำเนินการ โดยแยกค่าธรรมเนียมราชการและค่าใช้จ่ายภายนอกให้ชัดเจนก่อนเริ่มงาน" },
  { q: "ต้องพิมพ์ลายนิ้วมือด้วยตัวเองไหม", a: "คนไทย/ชาวต่างชาติที่อยู่ในไทยต้องมาพิมพ์ลายนิ้วมือด้วยตัวเองที่ปทุมวัน หรือให้เรา escort ไป; ถ้าอยู่ต่างประเทศใช้บริการ Overseas via POA — พิมพ์นิ้วมือกับตำรวจท้องถิ่นแล้วส่งมาที่เรา" },
  { q: "ใช้ยื่นวีซ่าอเมริกาได้ไหม", a: "ต้องตรวจประเภท PCC คำแปล และช่วงเวลาออกเอกสารจาก checklist ของหน่วยงานสหรัฐฯ ที่รับคำขอแต่ละประเภทก่อนยื่น" },
  { q: "ใช้ยื่น DHA ออสเตรเลียได้ไหม", a: "ต้องตรวจประเภทการเปิดเผยประวัติ รูปแบบคำแปล และอายุเอกสารจาก checklist ของ Department of Home Affairs สำหรับคำขอนั้น" },
  { q: "อายุการใช้งานเท่าไหร่", a: "ไม่มีช่วงเวลาเดียวสำหรับทุกประเทศ ให้ยึดวันที่และเงื่อนไขใน checklist ล่าสุดของหน่วยงานปลายทาง" },
  { q: "รับ PCC ต่างจังหวัดได้ไหม", a: "สอบถามพื้นที่และรูปแบบคำร้องก่อนเริ่มงาน หากจัดส่งได้จะแจ้งผู้ให้บริการ เงื่อนไข และเลขติดตาม" },
  { q: "ใช้ POA จากต่างประเทศได้ไหม", a: "ได้ — เรามี template POA ไทย-อังกฤษ ให้คุณไปรับรองกับสถานทูตไทยที่ประเทศคุณอยู่ (หรือ Notary + Apostille ในประเทศที่เป็นสมาชิก Hague) — เดินเรื่องแทนที่ปทุมวันได้เต็มรูปแบบ" },
  { q: "PCC + แปล + MFA + Embassy จบในที่เดียวไหม", a: "วางขอบเขตต่อเนื่องได้หากปลายทางต้องใช้หลายขั้น โดยต้องตรวจว่าต้องแปล รับรองกงสุล หรือรับรองสถานทูตจริงหรือไม่ก่อนเสนอราคา" },
  { q: "รับงานเสาร์-อาทิตย์ไหม", a: "เวลาของหน่วยงานราชการและเวลารับเอกสารอาจต่างกัน กรุณานัดหมายล่วงหน้า" },
  { q: "ข้อมูลส่วนตัวปลอดภัยไหม", a: "จำกัดการเข้าถึงข้อมูลแก่ผู้รับผิดชอบงาน และแจ้งเงื่อนไขการเก็บรักษาหรือลบข้อมูลในขอบเขตงาน" },
  { q: "รับ Corporate/HR/Migration agent ไหม", a: "รองรับงานองค์กร โดยประเมินจำนวนผู้ขอ เอกสาร และรูปแบบรายงานก่อนเสนอขอบเขต" },
];

const CASE_STUDIES = [
  "กรณีมีกำหนดนัดต่างประเทศ ควรตรวจประเภท PCC เอกสาร และเวลาของหน่วยงานก่อนยืนยันแผน",
  "กรณีวีซ่าทักษะออสเตรเลีย ต้องตรวจประเภทการเปิดเผยประวัติและคุณสมบัติผู้แปลตาม checklist ล่าสุด",
  "กรณีองค์กรส่งพนักงานหลายคน ควรทำบัญชีรายชื่อ เอกสาร และประเทศปลายทางก่อนประเมินเวลา",
  "กรณีผู้ขออยู่ต่างประเทศ ต้องตรวจแบบลายนิ้วมือ หนังสือมอบอำนาจ และวิธีส่งต้นฉบับก่อนยื่น",
  "กรณี K-1 fiancé visa ต้องตรวจข้อกำหนด PCC และคำแปลจาก checklist ของหน่วยงานผู้รับ",
];

const BENEFIT_ROWS = [
  ["ตรวจขั้นตอนก่อนยื่น", "ยืนยันแบบคำร้อง เอกสาร และจุดยื่นกับหน่วยงาน"],
  ["ระยะเวลาดำเนินการ", "ยืนยันตามประเภทคำร้อง คิว และขั้นตอนของหน่วยงาน"],
  ["ราคาโปร่งใส", "แยกค่าบริการ ค่าราชการ และค่าใช้จ่ายภายนอกก่อนเริ่มงาน"],
  ["ขอบเขตต่อเนื่อง", "ประเมิน PCC คำแปล MFA และสถานทูตตามข้อกำหนดปลายทาง"],
  ["Fingerprinting", "ตรวจสถานที่และวิธีพิมพ์ลายนิ้วมือตามประเภทคำร้อง"],
  ["ทีมสองภาษา", "ล่ามอังกฤษประจำจุด + เอกสารสองภาษา"],
  ["Data handling", "จำกัดผู้เข้าถึงและแจ้งเงื่อนไขเก็บรักษาในขอบเขตงาน"],
  ["Corporate-Ready", "ประเมินงานกลุ่มตามจำนวนผู้ขอและเอกสาร"],
];

// ── Content generator ────────────────────────────────────────────────────
export interface PccPageContext {
  service?: PccService;
  city?: PccCity;
  destination?: { slug: string; nameTh: string; nameEn: string; note: string };
  locale: "th" | "en" | "zh" | "ja" | "ko" | "vi" | "id";
}

export interface PccSection { heading: string; html: string; }
export interface PccPageContent {
  h1: string;
  intro: string;
  sections: PccSection[];
  faqs: Array<{ q: string; a: string }>;
  steps: Array<{ tag: string; text: string }>;
  benefits: string[][];
  wordCount: number;
}

function countWords(s: string): number {
  const thai = (s.match(/[\u0E00-\u0E7F]/g) ?? []).length;
  const en = (s.match(/[A-Za-z]+/g) ?? []).length;
  return Math.floor(thai / 3) + en;
}

function buildSectionHtml(heading: string, ctx: PccPageContext, rng: () => number): string {
  const { service, city, destination } = ctx;
  const paragraphs: string[] = [];
  const cityLabel = city ? `${city.nameTh} (${city.nameEn})` : "กรุงเทพและพื้นที่ที่ยืนยันให้บริการ";
  const svcLabel = service ? `${service.nameTh}` : "บริการขอใบตรวจประวัติอาชญากรรม";

  paragraphs.push(
    pick(rng, [
      `<strong>${heading}</strong> เป็นหัวข้อสำคัญในกระบวนการขอ PCC สำหรับ${svcLabel} เนื้อหานี้สรุปเอกสาร ขั้นตอน และจุดที่ควรตรวจสอบก่อนยื่นคำร้อง`,
      `เมื่อพูดถึง <strong>${heading}</strong> ในบริบทของ${svcLabel} สิ่งสำคัญคือรายละเอียดขั้นตอนที่กระทบเวลาและความครบถ้วนของเอกสาร`,
      `${heading} — คู่มือเตรียมคำร้อง PCC สำหรับผู้ที่ต้องการ${svcLabel}ใน${cityLabel}`,
    ]),
  );

  const factsPool = [
    `สถานที่ ช่องทาง และเวลารับคำร้องอาจเปลี่ยนได้ ควรตรวจประกาศของกองทะเบียนประวัติอาชญากรก่อนเดินทาง`,
    `ค่าธรรมเนียมราชการและค่าใช้จ่ายภายนอกจะแยกแสดงจากค่าบริการก่อนเริ่มงาน`,
    `ประเภทหนังสือรับรองและขอบเขตข้อมูลต้องเลือกให้ตรงกับวัตถุประสงค์และข้อกำหนดของหน่วยงานผู้รับ`,
    `บางปลายทางกำหนดรายละเอียดการเปิดเผยประวัติแตกต่างกัน จึงต้องตรวจ checklist ของคำขอนั้นโดยตรง`,
    `อายุเอกสารไม่มีเกณฑ์เดียวสำหรับทุกประเทศ ให้ยึดเงื่อนไขล่าสุดของหน่วยงานผู้รับ`,
    `ผู้ร้องต้องพิมพ์ลายนิ้วมือ 10 นิ้ว (rolled prints) — inkless หรือ ink card ตามที่ปลายทางระบุ`,
    `ชาวต่างชาติต้องแนบสำเนาพาสปอร์ต + วีซ่า/work permit หน้าที่ประทับเข้า-ออกล่าสุด`,
    `ระยะเวลาดำเนินการขึ้นกับประเภทคำร้อง คิว และขั้นตอนของหน่วยงาน ควรตรวจสอบก่อนกำหนดวันใช้งาน`,
    `สำหรับ overseas ใช้ Power of Attorney (POA) รับรองที่สถานทูตไทย หรือ Notary + Apostille ที่ประเทศต้นทาง`,
    `ระยะเวลารับรองกงสุลและสถานทูตขึ้นกับประเภทเอกสาร ระบบนัดหมาย และคิวของแต่ละหน่วยงาน`,
  ];
  paragraphs.push(`<ul>${shuffle(rng, factsPool).slice(0, 5).map((f) => `<li>${f}</li>`).join("")}</ul>`);

  if (destination) {
    paragraphs.push(`<p><strong>สำหรับปลายทาง ${destination.nameTh} (${destination.nameEn})</strong>: ${destination.note} — ควรตรวจ checklist ทางการอีกครั้งก่อนยื่น เพราะข้อกำหนดอาจเปลี่ยนได้</p>`);
  }
  if (service) {
    paragraphs.push(`<p><strong>ขอบเขต ${service.nameTh}</strong> อาจรวม: ${service.includes.join(", ")} — ทีมจะยืนยันรายการ ราคา และกำหนดส่งหลังตรวจเอกสาร</p>`);
  }

  paragraphs.push(
    pick(rng, [
      `<p>คำแนะนำ: จองคิวล่วงหน้าอย่างน้อย 2 วันทำการเพื่อได้ช่วงเช้า — ช่วยลดเวลารอที่หน้าเคาน์เตอร์ (ขึ้นกับคิวของหน่วยงานในแต่ละวัน)</p>`,
      `<p>คำแนะนำ: ตรวจสะกดชื่อพาสปอร์ตให้ตรง character-by-character กับฟอร์มปลายทาง — ผิด 1 ตัวอักษรอาจถูก reject ที่สถานทูต</p>`,
      `<p>คำแนะนำ: ถ้าปลายทางไม่ระบุประเภท ให้สอบถามหน่วยงานผู้รับก่อนเลือก Type 1 หรือ Type 2</p>`,
    ]),
  );

  return paragraphs.join("\n");
}

export function generatePccPage(ctx: PccPageContext): PccPageContent {
  const { service, city, destination, locale } = ctx;
  const seed = `pcc|${service?.slug ?? "pillar"}|${city?.slug ?? "global"}|${destination?.slug ?? "any"}|${locale}`;
  const rng = makeRng(seed);

  const cityLabel = city ? `${city.nameTh} (${city.nameEn})` : "กรุงเทพและพื้นที่ที่ยืนยันให้บริการ";
  const angle = pick(rng, HERO_ANGLES);
  const h1 = service && city
    ? `${service.nameTh} ที่ ${cityLabel} — ${angle}`
    : service && destination
      ? `${service.nameTh} สำหรับปลายทาง ${destination.nameTh} — ${angle}`
      : service
        ? `${service.nameTh} · ${angle}`
        : city
          ? `ขอใบตรวจประวัติอาชญากรรม (PCC) ใน ${cityLabel} — ${angle}`
          : destination
            ? `ขอใบตรวจประวัติอาชญากรรม (PCC) สำหรับ ${destination.nameTh} — ${angle}`
            : `บริการขอใบตรวจประวัติอาชญากรรม (Police Clearance Certificate) ไทย · ${angle}`;

  const trust = pick(rng, TRUST_HOOKS);
  const priceLine = service
    ? `ทีมจะยืนยันขอบเขต ราคา และระยะเวลาหลังตรวจเอกสาร — เหมาะสำหรับ ${service.audience.join(", ")}`
    : `ทีมจะประเมินบริการเสริม เช่น การพิมพ์ลายนิ้วมือ การนัดหมาย และการจัดส่ง ตามข้อเท็จจริงของผู้ขอ`;

  const intro = [
    `Thai Notary Law ให้คำปรึกษาและช่วยจัดเตรียมคำขอใบตรวจประวัติอาชญากรรม (PCC — Police Clearance Certificate หรือหนังสือรับรองความประพฤติ) สำหรับคนไทยและชาวต่างชาติ โดยตรวจเงื่อนไขเป็นรายกรณี`,
    `หน้านี้เน้น${service ? ` <strong>${service.nameTh}</strong>` : "บริการ PCC"}${city ? ` ในพื้นที่ <strong>${cityLabel}</strong>` : ""}${destination ? ` สำหรับปลายทาง <strong>${destination.nameTh} (${destination.nameEn})</strong>` : ""} — ${priceLine}`,
    trust,
    `ขอบเขตงานระบุผู้รับผิดชอบ จุดติดตาม เอกสารที่ต้องใช้ และขั้นตอนภายนอกที่ขึ้นกับหน่วยงานก่อนเริ่มดำเนินการ`,
  ].join(" ");

  const chosenH2s = shuffle(rng, H2_POOL).slice(0, 8);
  const sections: PccSection[] = chosenH2s.map((heading) => ({
    heading,
    html: buildSectionHtml(heading, ctx, rng),
  }));

  const steps = shuffle(rng, STEP_POOL).slice(0, 8).map((s) => ({ tag: s.tag, text: s.th }));
  const faqs = shuffle(rng, FAQ_POOL).slice(0, 8);
  const benefits = shuffle(rng, BENEFIT_ROWS).slice(0, 6);

  // Insert a case-study section deterministically
  const caseStudy = pick(rng, CASE_STUDIES);
  sections.push({
    heading: "ตัวอย่างสถานการณ์และจุดที่ควรตรวจสอบ",
    html: `<blockquote class="border-l-4 border-blue-600 bg-blue-50 p-4 italic">${caseStudy}</blockquote>`,
  });

  const wordCount =
    countWords(h1) + countWords(intro) +
    sections.reduce((n, s) => n + countWords(s.heading) + countWords(s.html), 0) +
    faqs.reduce((n, f) => n + countWords(f.q) + countWords(f.a), 0) +
    steps.reduce((n, s) => n + countWords(s.text), 0);

  return { h1, intro, sections, faqs, steps, benefits, wordCount };
}
